import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import useAssignmentStore from '../stores/useAssignmentStore';
import { useToast } from '../components/ToastContainer';
import useAuthStore from '../stores/useAuthStore';

const InstructorAssignmentCreatePage = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const toast = useToast();
  const { lessonId, assignmentId } = useParams();
  const {
    assignment,
    fetchAssignment,
    createAssignment,
    updateAssignment,
    deleteAssignment,
    isLoading,
    error
  } = useAssignmentStore();

  const [isEditing, setIsEditing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    instructions: '',
    dueDate: '',
    points: 100,
    allowLateSubmissions: false,
    submissionTypes: ['file'] // 'file', 'text', 'link'
  });

  useEffect(() => {
    if (assignmentId) {
      setIsEditing(true);
      fetchAssignment(assignmentId);
    }
  }, [assignmentId, fetchAssignment]);

  useEffect(() => {
    if (assignment && isEditing) {
      setFormData({
        title: assignment?.title || '',
        description: assignment?.description || '',
        instructions: assignment?.instructions || '',
        dueDate: assignment?.dueDate ? new Date(assignment?.dueDate).toISOString().split('T')[0] : '',
        points: assignment?.points || 100,
        allowLateSubmissions: assignment?.allowLateSubmissions || false,
        submissionTypes: assignment?.submissionTypes || ['file']
      });
    }
  }, [assignment, isEditing]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : name === 'points' ? Number(value) : value
    }));
  };

  const handleSubmissionTypeChange = (type) => {
    setFormData(prev => {
      const types = [...prev.submissionTypes];
      if (types.includes(type)) {
        return { ...prev, submissionTypes: types.filter(t => t !== type) };
      } else {
        return { ...prev, submissionTypes: [...types, type] };
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    if (!formData.title.trim()) {
      toast.error('Please enter an assignment title');
      setIsSubmitting(false);
      return;
    }
    if (!formData.dueDate) {
      toast.error('Please set a due date');
      setIsSubmitting(false);
      return;
    }

    try {
      const payload = {
        ...formData
      };

      let result;
      if (isEditing && assignmentId) {
        result = await updateAssignment(assignmentId, payload);
        if (result?.assignment?._id) {
          localStorage.setItem(`lesson-${lessonId}-assignment`, result?.assignment?._id);
        }
      } else {
        result = await createAssignment(payload);
        if (result?.assignment?._id) {
          localStorage.setItem(`lesson-${lessonId}-assignment`, result?.assignment?._id);
          setIsEditing(true);
          // Navigate to edit mode with assignmentId
          navigate(`/instructor/edit-assignment/${lessonId}/${result?.assignment?._id}`, { replace: true });
          return;
        }
      }

      if (result?.success) {
        toast.success(`${isEditing ? 'Assignment updated' : 'Assignment created'} successfully!`);
      } else {
        toast.error(`Failed to ${isEditing ? 'update' : 'create'} assignment`);
      }
    } catch (error) {
      console.error('Error saving assignment:', error);
      toast.error('An unexpected error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (error) {
    return (
      <DashboardLayout userRole={user.role}>
        <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900 flex items-center justify-center">
          <div className="text-center">
            <h2 className="text-xl font-semibold text-neutral-900 dark:text-white mb-2">Error loading data</h2>
            <p className="text-neutral-600 dark:text-neutral-400 mb-4">{error}</p>
            <button
              onClick={() => {
                fetchAssignment(assignmentId);
              }}
              className="px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              Retry
            </button>
          </div>
        </div>
      </DashboardLayout>
    );
  }


  return (
    <DashboardLayout userRole={user.role}>
      <div className="max-w-6xl mx-auto p-6 relative">
        {isLoading && (
          <div className="absolute inset-0 backdrop-blur-sm bg-black/30 flex flex-col items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary-500 border-t-transparent"></div>
            <p className="mt-3 text-white text-sm font-medium">Loading...</p>
          </div>
        )}
        {/* Header */}
        <div className="mb-6 flex justify-between items-center">
          <div>
            <button
              onClick={() => {
                navigate(-1);
              }}
              className="flex items-center text-primary-600 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-300 mb-2"
            >
              <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Back to Course Builder
            </button>
            <h1 className="text-2xl font-bold text-neutral-900 dark:text-white">
              {isEditing ? 'Edit Assignment' : 'Create New Assignment'}
            </h1>
          </div>
        </div>

        {/* Two-Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* LEFT COLUMN: Assignment Preview */}
          <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-6">
            <h2 className="text-lg font-semibold text-neutral-900 dark:text-white mb-4">Assignment Preview</h2>
            <div className="space-y-4">
              <div>
                <h3 className="font-medium text-neutral-900 dark:text-white">{formData.title || 'Untitled Assignment'}</h3>
                <p className="text-neutral-600 dark:text-neutral-400 mt-1">{formData.description || 'No description'}</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-neutral-500 dark:text-neutral-400">Due Date</p>
                  <p className="font-medium">{formData.dueDate ? new Date(formData.dueDate).toLocaleDateString() : 'Not set'}</p>
                </div>
                <div>
                  <p className="text-sm text-neutral-500 dark:text-neutral-400">Points</p>
                  <p className="font-medium">{formData.points} pts</p>
                </div>
                <div>
                  <p className="text-sm text-neutral-500 dark:text-neutral-400">Late Submissions</p>
                  <p className="font-medium">{formData.allowLateSubmissions ? 'Allowed' : 'Not allowed'}</p>
                </div>
                <div>
                  <p className="text-sm text-neutral-500 dark:text-neutral-400">Submission Types</p>
                  <p className="font-medium">{formData.submissionTypes.join(', ')}</p>
                </div>
              </div>

              {formData.instructions && (
                <div>
                  <p className="text-sm text-neutral-500 dark:text-neutral-400 mb-1">Instructions</p>
                  <p className="text-neutral-600 dark:text-neutral-400">{formData.instructions}</p>
                </div>
              )}
            </div>
          </div>

          {/* RIGHT COLUMN: Assignment Form */}
          <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">Title *</label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">Due Date *</label>
                  <input
                    type="date"
                    name="dueDate"
                    value={formData.dueDate}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">Points</label>
                  <input
                    type="number"
                    name="points"
                    min="0"
                    value={formData.points}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">Submission Types</label>
                  <div className="flex gap-4 mt-2">
                    {['file', 'text', 'link'].map(type => (
                      <label key={type} className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.submissionTypes.includes(type)}
                          onChange={() => handleSubmissionTypeChange(type)}
                          className="h-4 w-4 text-primary-600 focus:ring-primary-500"
                        />
                        <span className="ml-2 text-sm text-neutral-700 dark:text-neutral-300 capitalize">{type}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">Description</label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    rows={2}
                    className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">Instructions</label>
                  <textarea
                    name="instructions"
                    value={formData.instructions}
                    onChange={handleInputChange}
                    rows={4}
                    className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div className="md:col-span-2 flex items-center">
                  <input
                    type="checkbox"
                    name="allowLateSubmissions"
                    checked={formData.allowLateSubmissions}
                    onChange={handleInputChange}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500"
                  />
                  <label className="ml-2 block text-sm text-neutral-700 dark:text-neutral-300">
                    Allow late submissions
                  </label>
                </div>
              </div>

              {isEditing && assignmentId && (
                <div className="pt-4">
                  <button
                    type="button"
                    onClick={async () => {
                      if (window.confirm('Are you sure you want to delete this assignment? This cannot be undone.')) {
                        const result = await deleteAssignment(assignmentId);
                        if (result?.success) {
                          toast.success('Assignment deleted successfully');
                          navigate(-1);
                        }
                      }
                    }}
                    className="px-4 py-2 bg-danger-500 hover:bg-danger-600 text-white text-sm font-medium rounded-lg shadow-sm"
                  >
                    Delete Assignment
                  </button>
                </div>
              )}

              <div className="flex justify-end pt-4 border-t border-neutral-200 dark:border-neutral-700">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-6 py-3 bg-primary-500 hover:bg-primary-600 disabled:bg-primary-300 text-white font-medium rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  {isSubmitting ? 'Saving...' : isEditing ? 'Update Assignment' : 'Create Assignment'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default InstructorAssignmentCreatePage;


